import { ErrorCode, i18nKey, ROLE_NONE, ROLE_OWNER, stripSuspendedGate, } from "@shared";
import { hasAccessGateChanged } from "./group.svelte";
import { mergeKeepingOnlyChanged } from "./object";
import { hasOwnerRights, isPermitted } from "./permissions";
export function canChangeRoles({ membership, permissions }, currRole, newRole) {
    if (currRole === newRole) {
        return false;
    }
    switch (newRole) {
        case ROLE_OWNER:
            return hasOwnerRights(membership.role);
        default:
            return isPermitted(membership.role, permissions.changeRoles);
    }
}
export function canEditCommunity({ membership, permissions }) {
    return isPermitted(membership.role, permissions.updateDetails);
}
export function canCreatePublicChannel({ membership, permissions }) {
    return isPermitted(membership.role, permissions.createPublicChannel);
}
export function canCreatePrivateChannel({ membership, permissions }) {
    return isPermitted(membership.role, permissions.createPrivateChannel);
}
export function canManageUserGroups({ membership, permissions }) {
    return isPermitted(membership.role, permissions.manageUserGroups);
}
export function canChangeCommunityRoles({ membership, permissions }) {
    return isPermitted(membership.role, permissions.changeRoles);
}
export function canChangeCommunityPermissions({ membership }) {
    return hasOwnerRights(membership.role);
}
export function isCommunityLapsed({ membership }) {
    return membership.lapsed;
}
// A community the user hasn't joined (or whose membership has lapsed) hides its channels'
// messages unless it is public and ungated (a suspended unique person gate counts as no gate)
export function isPrivateCommunityPreview(community) {
    return (community !== undefined &&
        (community.membership.role === ROLE_NONE || community.membership.lapsed) &&
        (!community.public || stripSuspendedGate(community.gateConfig.gate).kind !== "no_gate"));
}
export function canDeleteCommunity({ membership }) {
    return hasOwnerRights(membership.role);
}
export function canBlockUsers(community) {
    return (community.public &&
        isPermitted(community.membership.role, community.permissions.removeMembers));
}
export function canUnblockUsers(community) {
    return (community.public &&
        isPermitted(community.membership.role, community.permissions.removeMembers));
}
export function canInviteUsers(community) {
    return isPermitted(community.membership.role, community.permissions.inviteUsers);
}
export function canRemoveMembers(community) {
    return isPermitted(community.membership.role, community.permissions.removeMembers);
}
/**
 * What the user has changed about a community they are editing, holding only the fields that differ.
 * A community with no changes gives an empty object.
 */
export function communityChanges({ community: original, rules: originalRules }, { community: candidate, rules }) {
    const changes = {};
    if (candidate.name !== original.name)
        changes.name = candidate.name;
    if (candidate.description !== original.description) {
        changes.description = candidate.description;
    }
    if (rules.enabled !== originalRules.enabled || rules.text !== originalRules.text) {
        changes.rules = rules;
    }
    if (Object.keys(mergeKeepingOnlyChanged(original.permissions, candidate.permissions)).length > 0) {
        changes.permissions = candidate.permissions;
    }
    if (candidate.avatar?.blobUrl !== original.avatar?.blobUrl) {
        changes.avatar = candidate.avatar?.blobData;
    }
    if (candidate.banner?.blobUrl !== original.banner?.blobUrl) {
        changes.banner = candidate.banner?.blobData;
    }
    if (hasAccessGateChanged(candidate.gateConfig, original.gateConfig)) {
        changes.gateConfig = candidate.gateConfig;
    }
    if (candidate.public !== original.public)
        changes.isPublic = candidate.public;
    if (candidate.primaryLanguage !== original.primaryLanguage) {
        changes.primaryLanguage = candidate.primaryLanguage;
    }
    return changes;
}
// The first of the changes the user has yet to confirm, in the order they are asked. Only making a
// community private needs confirming, not making it public.
export function communityChangeConfirmationNeeded(original, changes, confirmed) {
    if (changes.name !== undefined && original.verified && !confirmed.includes("verified_name")) {
        return "verified_name";
    }
    if (changes.isPublic === false && !confirmed.includes("visibility")) {
        return "visibility";
    }
    return undefined;
}
function communityFailure(resp, fallback) {
    if (resp.kind === "offline")
        return i18nKey("offlineError");
    if (resp.kind === "error" && resp.code === ErrorCode.NameTaken) {
        return i18nKey("communities.errors.name_taken");
    }
    return i18nKey(fallback);
}
export function communityUpdateResult(resp) {
    switch (resp.kind) {
        case "success":
            return { kind: "success" };
        case "error":
        case "offline":
            return {
                kind: "failed",
                message: communityFailure(resp, "communities.errors.saveFailed"),
            };
        default:
            return resp;
    }
}
export function communityCreationFailure(resp) {
    return communityFailure(resp, "communities.errors.failure");
}
